export type Rect = { x: number; y: number; width: number; height: number };

export type MediaFitMode = "cover" | "contain";

export function computeCoverTransform(
  mediaW: number,
  mediaH: number,
  frameW: number,
  frameH: number
): { scale: number; x: number; y: number } {
  const mw = Math.max(1, Number(mediaW) || 1);
  const mh = Math.max(1, Number(mediaH) || 1);
  const fw = Math.max(1, Number(frameW) || 1);
  const fh = Math.max(1, Number(frameH) || 1);

  const scale = Math.max(fw / mw, fh / mh) || 1;
  const w = mw * scale;
  const h = mh * scale;
  const x = (fw - w) / 2;
  const y = (fh - h) / 2;
  return { scale, x, y };
}

/**
 * Contain mode: scale media to fit entirely inside frame (letterbox/pillarbox allowed).
 * No cropping - full media is visible with potential empty space on sides.
 */
export function computeContainTransform(
  mediaW: number,
  mediaH: number,
  frameW: number,
  frameH: number
): { scale: number; x: number; y: number } {
  const mw = Math.max(1, Number(mediaW) || 1);
  const mh = Math.max(1, Number(mediaH) || 1);
  const fw = Math.max(1, Number(frameW) || 1);
  const fh = Math.max(1, Number(frameH) || 1);

  const scale = Math.min(fw / mw, fh / mh) || 1;
  const w = mw * scale;
  const h = mh * scale;
  const x = (fw - w) / 2;
  const y = (fh - h) / 2;
  return { scale, x, y };
}

/**
 * Unified transform function supporting both contain and cover modes.
 */
export function computeMediaTransform(
  mediaW: number,
  mediaH: number,
  frameW: number,
  frameH: number,
  mode: MediaFitMode = "cover"
): { scale: number; x: number; y: number; width: number; height: number } {
  const mw = Math.max(1, Number(mediaW) || 1);
  const mh = Math.max(1, Number(mediaH) || 1);
  const fw = Math.max(1, Number(frameW) || 1);
  const fh = Math.max(1, Number(frameH) || 1);

  const scale = mode === "contain"
    ? Math.min(fw / mw, fh / mh) || 1
    : Math.max(fw / mw, fh / mh) || 1;

  const w = mw * scale;
  const h = mh * scale;
  const x = (fw - w) / 2;
  const y = (fh - h) / 2;
  return { scale, x, y, width: w, height: h };
}

export function computeSafeZoneRect(args: {
  canvasW: number;
  canvasH: number;
  marginFrac?: number; // default 0.06
}): Rect {
  const w = Math.max(1, Number(args.canvasW) || 1);
  const h = Math.max(1, Number(args.canvasH) || 1);
  const fracRaw = Number(args.marginFrac);
  const frac = Number.isFinite(fracRaw) ? Math.max(0, Math.min(0.45, fracRaw)) : 0.06;
  const mx = w * frac;
  const my = h * frac;
  return { x: mx, y: my, width: Math.max(1, w - mx * 2), height: Math.max(1, h - my * 2) };
}

export function computeMediaContentRect(args: {
  mediaW: number;
  mediaH: number;
  boundsW: number;
  boundsH: number;
  fit?: MediaFitMode;
  focalPoint?: { x: number; y: number }; // 0..1
  safePadding?: number; // px (applied to contain; cover ignores to avoid letterboxing)
}): Rect {
  const boundsW = Math.max(1, Number(args.boundsW) || 1);
  const boundsH = Math.max(1, Number(args.boundsH) || 1);
  const mediaW = Math.max(1, Number(args.mediaW) || 1);
  const mediaH = Math.max(1, Number(args.mediaH) || 1);

  const fit: MediaFitMode = args.fit === "contain" ? "contain" : "cover";
  const fxRaw = Number(args.focalPoint?.x);
  const fyRaw = Number(args.focalPoint?.y);
  const focalX = Number.isFinite(fxRaw) ? Math.max(0, Math.min(1, fxRaw)) : 0.5;
  const focalY = Number.isFinite(fyRaw) ? Math.max(0, Math.min(1, fyRaw)) : 0.5;

  const pad = Math.max(0, Number(args.safePadding) || 0);
  const innerW = fit === "contain" ? Math.max(1, boundsW - pad * 2) : boundsW;
  const innerH = fit === "contain" ? Math.max(1, boundsH - pad * 2) : boundsH;

  if (fit === "contain") {
    const s = Math.min(innerW / mediaW, innerH / mediaH) || 1;
    const w = mediaW * s;
    const h = mediaH * s;
    const x = pad + (innerW - w) / 2;
    const y = pad + (innerH - h) / 2;
    return { x, y, width: w, height: h };
  }

  // cover
  const s = Math.max(innerW / mediaW, innerH / mediaH) || 1;
  const w = mediaW * s;
  const h = mediaH * s;

  // Place focal point at center of bounds, then clamp to cover.
  let x = boundsW / 2 - focalX * w;
  let y = boundsH / 2 - focalY * h;

  const minX = boundsW - w;
  const minY = boundsH - h;
  x = Math.max(minX, Math.min(0, x));
  y = Math.max(minY, Math.min(0, y));

  return { x, y, width: w, height: h };
}

type WrapResult = { lines: string[]; truncated: boolean };

let measureCanvas: HTMLCanvasElement | null = null;
function getMeasureCtx(): CanvasRenderingContext2D | null {
  if (typeof document === "undefined") return null;
  if (!measureCanvas) measureCanvas = document.createElement("canvas");
  return measureCanvas.getContext("2d");
}

function buildFontString(args: {
  fontStyle?: string;
  fontWeight?: string | number;
  fontSize: number;
  fontFamily?: string;
}): string {
  const style = args.fontStyle ? String(args.fontStyle) : "normal";
  const weight = args.fontWeight ? String(args.fontWeight) : "400";
  const size = Math.max(1, Math.floor(args.fontSize));
  const family = args.fontFamily ? String(args.fontFamily) : "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial";
  return `${style} ${weight} ${size}px ${family}`;
}

export function measureTextBlock(args: {
  text: string;
  fontSize: number;
  fontFamily?: string;
  fontWeight?: string | number;
  fontStyle?: string;
  lineHeight?: number; // multiplier
}): { width: number; height: number; lines: number } {
  const ctx = getMeasureCtx();
  if (!ctx) return { width: 0, height: 0, lines: 0 };

  const fontSize = Math.max(1, Number(args.fontSize) || 1);
  const lineHeight = Number.isFinite(args.lineHeight as number) ? Math.max(0.8, Number(args.lineHeight)) : 1.15;

  ctx.font = buildFontString({
    fontFamily: args.fontFamily,
    fontSize,
    fontStyle: args.fontStyle,
    fontWeight: args.fontWeight,
  });

  const lines = String(args.text ?? "").split(/\r?\n/);
  let maxW = 0;
  for (const line of lines) {
    maxW = Math.max(maxW, ctx.measureText(line).width);
  }
  return { width: maxW, height: lines.length * fontSize * lineHeight, lines: lines.length };
}

function wrapTextToWidth(
  text: string,
  maxWidth: number,
  ctx: CanvasRenderingContext2D,
  opts?: { ellipsis?: boolean; maxLines?: number }
): WrapResult {
  const maxW = Math.max(1, maxWidth);
  const maxLines = Number.isFinite(opts?.maxLines as number) ? Math.max(1, Number(opts?.maxLines)) : Infinity;
  const raw = (text ?? "").toString();
  const paragraphs = raw.split(/\r?\n/);
  const outLines: string[] = [];
  let truncated = false;

  const measure = (s: string) => ctx.measureText(s).width;

  const pushLine = (line: string) => {
    if (outLines.length >= maxLines) return false;
    outLines.push(line);
    return true;
  };

  const breakWord = (word: string) => {
    let cur = "";
    for (const ch of word) {
      const next = cur + ch;
      if (measure(next) <= maxW || cur.length === 0) {
        cur = next;
      } else {
        if (!pushLine(cur)) return false;
        cur = ch;
      }
    }
    return pushLine(cur);
  };

  for (let p = 0; p < paragraphs.length; p++) {
    const para = paragraphs[p].trim();
    if (!para) {
      if (!pushLine("")) break;
      continue;
    }

    const words = para.split(/\s+/).filter(Boolean);
    let line = "";
    for (const word of words) {
      const next = line ? `${line} ${word}` : word;
      if (measure(next) <= maxW) {
        line = next;
        continue;
      }

      if (line) {
        if (!pushLine(line)) break;
        line = "";
      }

      if (measure(word) <= maxW) {
        line = word;
      } else {
        if (!breakWord(word)) break;
      }
    }

    if (line) {
      if (!pushLine(line)) break;
    }
  }

  if (outLines.length > maxLines) {
    outLines.length = maxLines;
    truncated = true;
  }

  if (opts?.ellipsis && maxLines !== Infinity && outLines.length >= maxLines) {
    // If we truncated, apply an ellipsis to the last line.
    const needsEllipsis = raw.replace(/\s+/g, " ").trim() !== outLines.join(" ").replace(/\s+/g, " ").trim();
    if (needsEllipsis) {
      truncated = true;
      const i = maxLines - 1;
      let base = outLines[i] ?? "";
      const ell = "…";
      while (base.length > 0 && measure(base + ell) > maxW) {
        base = base.slice(0, -1);
      }
      outLines[i] = base ? base + ell : ell;
    }
  }

  return { lines: outLines, truncated };
}

export function autoFitTextToBox(args: {
  text: string;
  boxW: number;
  boxH: number;
  fontFamily?: string;
  fontWeight?: string | number;
  fontStyle?: string;
  lineHeight?: number; // multiplier
  safePadding?: number; // px
  maxLines?: number;
  minFontSize?: number;
  maxFontSize?: number;
}): { text: string; fontSize: number } {
  const pad = Math.max(0, Number(args.safePadding) || 0);
  const w = Math.max(1, Number(args.boxW) - pad * 2);
  const h = Math.max(1, Number(args.boxH) - pad * 2);

  const maxLines = Number.isFinite(args.maxLines as number) ? Math.max(1, Number(args.maxLines)) : Infinity;
  const lineHeight = Number.isFinite(args.lineHeight as number) ? Math.max(0.8, Number(args.lineHeight)) : 1.15;

  const minFontSize = Number.isFinite(args.minFontSize as number) ? Math.max(4, Number(args.minFontSize)) : 10;
  const maxFontSize = Number.isFinite(args.maxFontSize as number) ? Math.max(minFontSize, Number(args.maxFontSize)) : 96;

  const ctx = getMeasureCtx();
  if (!ctx) {
    return { text: args.text, fontSize: Math.max(minFontSize, Math.min(maxFontSize, 24)) };
  }

  const rawText = (args.text ?? "").toString();
  let chosenSize = Math.max(minFontSize, Math.min(maxFontSize, 24));
  let chosenLines: string[] = [rawText];

  for (let size = maxFontSize; size >= minFontSize; size -= 1) {
    ctx.font = buildFontString({
      fontFamily: args.fontFamily,
      fontSize: size,
      fontStyle: args.fontStyle,
      fontWeight: args.fontWeight,
    });

    const wrap = wrapTextToWidth(rawText, w, ctx, { ellipsis: true, maxLines });
    const lines = wrap.lines.length ? wrap.lines : [""];
    const totalH = lines.length * size * lineHeight;

    if (lines.length <= maxLines && totalH <= h + 0.01) {
      chosenSize = size;
      chosenLines = lines;
      break;
    }

    chosenSize = size;
    chosenLines = lines;
  }

  return { text: chosenLines.join("\n"), fontSize: chosenSize };
}
